import { Box, Button, PageLoader } from '@customerio/pluma-components/react';
import { useRouter } from '@tanstack/react-router';
import { useIntegrationsWorkspaceId } from '../../workspace-id';
import { createPopularIntegrationsList, PopularIntegrations, type PopularIntegration } from '../../catalog/images';
import { ConnectionType } from '../../catalog/enums';
import { useProductTaxonomyTerms } from '../../directory/taxonomy';
import { ConnectionsBlankSlate } from '../../connections/components/connections-blank-slate';
import { ConnectionsTable } from '../../connections/components/connections-table';
import { useDeveloperConnectionsViewModel } from '../hooks';
import { ApiKeysSecurityCard } from './api-keys-security-card';

// The migrated URL, not the `journeys` one: this is an in-Hydra link, so it should not pay
// for the legacy handoff redirect.
const DIRECTORY_PATH = '/workspaces/$workspaceId/integrations/all/directory';

/** The 9 SDK/library shortcuts the empty state promotes (ported from the Ember developers controller). */
const POPULAR_DEVELOPER_INTEGRATIONS: PopularIntegration[] = [
  PopularIntegrations.javascript,
  PopularIntegrations.ios,
  PopularIntegrations.android,
  PopularIntegrations.reactNative,
  PopularIntegrations.flutter,
  PopularIntegrations.node,
  PopularIntegrations.python,
  PopularIntegrations.go,
  PopularIntegrations.http,
];

/** The connection types whose rows the ApiKeysSecurityCard appends (the mobile SDKs). */
const MOBILE_SDK_TYPES: readonly string[] = ['ios', 'android', 'reactnative'];

/**
 * The Developers sub-tab (the `/integrations/all/developers` route body). The
 * route is URL-only: it renders inside the integrations shell but has no entry
 * in the tab nav. The list is pipelines-only, restricted to Developer-type
 * connections; the "API keys & security" panel renders above it.
 */
export function DevelopersTab() {
  const router = useRouter();
  const workspaceId = String(useIntegrationsWorkspaceId());
  const terms = useProductTaxonomyTerms();
  const { connections, pipelinesConnectionsById, isLoading, shouldGroupAutoTrack } = useDeveloperConnectionsViewModel();

  if (isLoading) {
    return <PageLoader />;
  }

  if (connections.length === 0) {
    const popularIntegrations = createPopularIntegrationsList(workspaceId, POPULAR_DEVELOPER_INTEGRATIONS);
    // Cross-app deep-link into the Pipelines app: a full-page-load anchor, not a router link.
    // oxlint-disable-next-line local-rules/enforce-generate-path -- cross-app Pipelines (CDP) deep-link, not a Hydra router path
    const getStartedHref = `/workspaces/${workspaceId}/pipelines/sources/add`;
    return (
      <ConnectionsBlankSlate
        icon="editor-code"
        title={`Track ${terms.customerSingular} behavior and send targeted messages through your app or website.`}
        description={`Customer.io's API allows you to seamlessly identify, track, and message ${terms.customersPlural} natively in your app or website.`}
        popularIntegrationsText="Get started with one of our SDKs or libraries"
        popularIntegrations={popularIntegrations}
        workspaceId={workspaceId}
      >
        <Button
          href={getStartedHref}
          isExternal
          withSafeExternalAttributes={false}
          variant="primary"
          icon="chevron-right"
          iconPosition="trailing"
        >
          Get started
        </Button>
      </ConnectionsBlankSlate>
    );
  }

  const mobileConnections = connections.filter((connection) => MOBILE_SDK_TYPES.includes(connection.type));
  const addConnectionHref = router.buildLocation({
    to: DIRECTORY_PATH,
    params: { workspaceId },
    search: { selectedConnectionType: ConnectionType.developer },
  }).href;
  const addDataInHref = router.buildLocation({
    to: DIRECTORY_PATH,
    params: { workspaceId },
    search: { selectedType: 'Data in' },
  }).href;
  const addDataOutHref = router.buildLocation({
    to: DIRECTORY_PATH,
    params: { workspaceId },
    search: { selectedType: 'Data out' },
  }).href;

  return (
    <>
      <Box mb="250">
        <ApiKeysSecurityCard connections={mobileConnections} />
      </Box>
      <ConnectionsTable
        connections={connections}
        isSearchable
        addConnectionButtonHref={addConnectionHref}
        addDataInConnectionButtonHref={addDataInHref}
        addDataOutConnectionButtonHref={addDataOutHref}
        shouldGroupAutoTrack={shouldGroupAutoTrack}
        pipelinesConnectionsById={pipelinesConnectionsById}
        workspaceId={workspaceId}
      />
    </>
  );
}
